import first from 'lodash-es/first';
import keys from 'lodash-es/keys';

import { formatDate } from '@app/shared/date-format.pipe';

import { Office } from '../shared/office';
import { Provider } from '../shared/provider';

interface ApiV2AppointmentInventory {
  utc_start_datetime: string;
  timezone_tzinfo: string;
  duration: number;
  id: number;
  date: string;
}

export class AppointmentInventory {
  start_time: Date;
  timezone: string;
  duration: number;
  date: string;
  id: number;

  static fromApiV2({
    utc_start_datetime,
    timezone_tzinfo,
    duration,
    id,
    date,
  }: ApiV2AppointmentInventory): AppointmentInventory {
    const apptInv = new AppointmentInventory();
    apptInv.start_time = new Date(utc_start_datetime);
    apptInv.timezone = timezone_tzinfo;
    apptInv.duration = duration;
    apptInv.id = id;
    apptInv.date = date;
    return apptInv;
  }

  static fromGraphQL({
    utcStartDatetime,
    timezoneTzinfo,
    duration,
    date,
    id,
  }: Record<string, any>): AppointmentInventory {
    const apptInv = new AppointmentInventory();
    apptInv.start_time = new Date(utcStartDatetime);

    apptInv.timezone = timezoneTzinfo;
    apptInv.duration = duration;
    apptInv.date = date;
    apptInv.id = id;
    return apptInv;
  }
}

export type DayInventories = Record<string, AppointmentInventory[]>;

export class ProviderInventories {
  provider: Provider;
  office: Office;
  dayInventories: DayInventories;
  nextInventory: AppointmentInventory;
  hasInventories: boolean;

  static fromApiV2({
    office,
    provider,
    next_inventory,
    appointment_inventories_by_date,
  }: Record<string, any>): ProviderInventories {
    const provInv = new ProviderInventories();
    provInv.office = Office.fromApiV2(office);
    provInv.provider = Provider.fromApiV2(provider);
    provInv.nextInventory = AppointmentInventory.fromApiV2(next_inventory);
    provInv.dayInventories = {};
    const inventoriesByDate = appointment_inventories_by_date;
    provInv.hasInventories = inventoriesByDate && keys(inventoriesByDate).length > 0;
    keys(inventoriesByDate).forEach(
      date =>
        (provInv.dayInventories[formatDate(date, 'yyyy-MM-dd')] = inventoriesByDate[
          date
        ].map((dayInv: ApiV2AppointmentInventory) => AppointmentInventory.fromApiV2(dayInv))),
    );
    return provInv;
  }
}

// These two utility functions were originally part of next-remote-visit component
// and were extracted to share the logic in other areas (i.e. for analytics events)
// returns a yyyy-MM-dd formatted date string
export function firstInventoryDateAvailable(inventories: DayInventories): string {
  return first(Object.keys(inventories));
}

export function getFirstDateInventories(inventories: DayInventories): AppointmentInventory[] {
  const firstDate = firstInventoryDateAvailable(inventories);
  return Object.values(inventories[firstDate]);
}
